import {
  CartProductCode,
  CurrentPayPlan,
  IMonthlyPaymentDetail,
  MSSMemberStatus,
  MembershipLevel,
  MembershipProductType,
  RoleType,
} from '@ace-clublabs/common-membership-api-types';
import { GENDER_CODE, IFormData, IMemberDetails, IState } from './model';
import { ICalculateCostRequest, IPaymentInfo, IPriceCart, MSSProductStatus } from '@lib/types';
import { getAwsDomainUrl } from '../utils/generateURL';
import { STATE_KEY } from './state';
import { initialState } from './stubs';
import { Icon } from '@ace-clublabs/shared-component-library';

export const autoPayBillPlans = [
  CurrentPayPlan.RECURRINGCREDITORDEBITCARD,
  CurrentPayPlan.RECURRINGELECTRONICCHECK,
  CurrentPayPlan.RECURRINGMONTHLY,
];

export const annualAutoPayBillPlans = [
  CurrentPayPlan.RECURRINGCREDITORDEBITCARD,
  CurrentPayPlan.RECURRINGELECTRONICCHECK,
];

export const isAutopayBillPlan = (billPlan: CurrentPayPlan): boolean => {
  return autoPayBillPlans.includes(billPlan);
};

export const isOnAnnualAutopayBillPlan = (billPlan: CurrentPayPlan): boolean => {
  return annualAutoPayBillPlans.includes(billPlan);
};

export const isOnMonthlyBillPlan = (billPlan: CurrentPayPlan): boolean => {
  return billPlan === CurrentPayPlan.RECURRINGMONTHLY;
};

export const getOrderedMonthlyPayments = (monthlyPayments: IMonthlyPaymentDetail[]): IMonthlyPaymentDetail[] => {
  return monthlyPayments.sort((a, b) => {
    //@ts-ignore
    return new Date(a.dueDate) - new Date(b.dueDate);
  });
};

//Payment panel
export const getAmountDue = (
  memberDetails: IMemberDetails,
  monthlyPaymentData: IPaymentInfo['monthlyPaymentData'],
  priceCart: IPriceCart
): number => {
  let monthlyPayments, monthlyPaymentAmount;
  const { hasPaymentInTransit, paymentInTransitDetails, currentBillPlan } = memberDetails;
  const isMonthlyBillPlan = isOnMonthlyBillPlan(currentBillPlan as CurrentPayPlan);

  if (isMonthlyBillPlan) {
    monthlyPayments = getOrderedMonthlyPayments(monthlyPaymentData?.payments || []);
    monthlyPaymentAmount = monthlyPayments[0]?.amount;
  }

  // MDO - map amount due from monthly payments array
  // ADO - map amount due from price cart
  const originalAmountDue = isMonthlyBillPlan ? monthlyPaymentAmount ?? 0 : priceCart.total ?? 0;
  if (hasPaymentInTransit && paymentInTransitDetails!.length > 0) {
    return 0;
  } else {
    const totalAmountDue = originalAmountDue && originalAmountDue > 0 ? originalAmountDue : 0;
    return totalAmountDue;
  }
};

export const getCurrentMonthlyPaymentDetails = (monthlyPaymentData: IPaymentInfo['monthlyPaymentData']) => {
  const monthlyPayments = getOrderedMonthlyPayments(monthlyPaymentData?.payments || []);
  const monthlyPaymentAmount = monthlyPayments[0]?.amount;
  const monthlyPaymentDueDate = monthlyPayments[0]?.dueDate;
  const monthlyPastDue = monthlyPayments[0]?.isPastDue;
  const monthlyPaymentPastDue = isPastDue(monthlyPaymentDueDate);

  return {
    monthlyPaymentAmount,
    monthlyPaymentDueDate,
    monthlyPaymentPastDue: monthlyPaymentPastDue
      ? monthlyPastDue === false
        ? false //handles when API returns old monthly payment data that should not show as past due
        : monthlyPaymentPastDue
      : monthlyPaymentPastDue,
  };
};

export const getJestStateObj = (mockState: IState) => {
  const keys = Object.keys(mockState);

  const jestStateObj = {} as any;

  for (let key of keys) {
    jestStateObj[key] = [mockState[key as keyof IState]];
  }

  return jestStateObj;
};

export const getGenderList = (memberDetails: IMemberDetails | null) => {
  const genderList: { genderName: string; genderValue: string }[] = [];
  for (const g of memberDetails?.prospectiveMemberRules?.allowedGenders ?? []) {
    genderList.push({ genderName: GENDER_CODE[g], genderValue: g });
  }

  return genderList;
};

export const getIsBenefitLevelUpgradeAllowed = (
  memberDetails: IMemberDetails,
  currentBenefitLevel?: MembershipLevel
): boolean => {
  let upgradesAllowedBasedOnCurrentLevel: boolean = false;
  const upgradesAllowed = memberDetails?.prospectiveMemberRules?.upgradesAllowed;

  const currentLevel = currentBenefitLevel || memberDetails?.benefitLevels?.existingLevel;
  const allowedMembershipLevels = memberDetails?.prospectiveMemberRules?.allowedMembershipLevels;

  if (currentLevel === MembershipLevel.CLASSIC) {
    upgradesAllowedBasedOnCurrentLevel =
      allowedMembershipLevels.includes(MembershipLevel.PLUS) ||
      allowedMembershipLevels.includes(MembershipLevel.PREMIER);
  } else if (currentLevel === MembershipLevel.PLUS) {
    upgradesAllowedBasedOnCurrentLevel = allowedMembershipLevels.includes(MembershipLevel.PREMIER);
  }

  return upgradesAllowed && upgradesAllowedBasedOnCurrentLevel;
};

export const getIsBenefitLevelDowngradeAllowed = (
  memberDetails: IMemberDetails,
  currentBenefitLevel?: MembershipLevel
): boolean => {
  let downgradesAllowedBasedOnCurrentLevel: boolean = false;
  const downgradesAllowed = memberDetails?.prospectiveMemberRules?.downgradesAllowed;

  const currentLevel = currentBenefitLevel || memberDetails?.benefitLevels?.existingLevel;
  const allowedMembershipLevels = memberDetails?.prospectiveMemberRules?.allowedMembershipLevels;

  if (currentLevel === MembershipLevel.PLUS) {
    downgradesAllowedBasedOnCurrentLevel = allowedMembershipLevels.includes(MembershipLevel.CLASSIC);
  } else if (currentLevel === MembershipLevel.PREMIER) {
    downgradesAllowedBasedOnCurrentLevel =
      allowedMembershipLevels.includes(MembershipLevel.PLUS) ||
      allowedMembershipLevels.includes(MembershipLevel.CLASSIC);
  }

  return downgradesAllowed && downgradesAllowedBasedOnCurrentLevel;
};

export const getMaximumAssociatesAllowed = (memberDetails: IMemberDetails) => {
  const memberRoles = memberDetails.prospectiveMemberRules.memberRoles;
  let maxAssociateAllowed = 0;

  for (const rule of memberRoles) {
    if (rule.roleType !== RoleType.PRIMARY) {
      maxAssociateAllowed += rule.maximumAllowed;
    }
  }

  return maxAssociateAllowed;
};

export const getNumberOfAssociates = (memberDetails: IMemberDetails) => {
  const members = memberDetails.members;
  let associateCount = 0;

  if (members) {
    associateCount = members.filter(
      (member) => member.roleType !== RoleType.PRIMARY && member.status !== MSSMemberStatus.REMOVED
    ).length;
  }

  return associateCount;
};

export const isAddFamilyMembersAllowed = (memberDetails: IMemberDetails) => {
  const isUpgradeAllowed = memberDetails?.prospectiveMemberRules?.upgradesAllowed;

  const maxAssociateAllowed = getMaximumAssociatesAllowed(memberDetails);
  const numberOfAssociates = getNumberOfAssociates(memberDetails);

  return isUpgradeAllowed && numberOfAssociates < maxAssociateAllowed;
};

export const getHasReachedRoleTypeMax = (roleType: RoleType, memberDetails: IMemberDetails, formData: IFormData) => {
  const maximumAllowed =
    memberDetails?.prospectiveMemberRules?.memberRoles?.find((role) => role.roleType === roleType)?.maximumAllowed || 0;

  const totalNumOfAssociates = formData.members?.filter(
    // @ts-ignore
    (member) => {
      if (member.roleType !== roleType) {
        return false;
      }

      if (roleType === RoleType.ADULT_ASSOCIATE) {
        return true;
      }

      return member.status !== MSSMemberStatus.REMOVED;
    }
  ).length;

  // @ts-ignore
  return totalNumOfAssociates >= maximumAllowed;
};

export const getLoggedInMember = (memberDetails: IMemberDetails) => {
  return memberDetails.members?.find((mem) => mem.customerId === memberDetails.loggedInCustomerId);
};

export const generateInitialFormData = (memberDetails: IMemberDetails) => {
  const formData: IFormData = {
    requestedBillPlan: memberDetails?.currentBillPlan,
    requestedMembershipLevel: memberDetails?.benefitLevels?.existingLevel,
    members: JSON.parse(JSON.stringify(memberDetails?.members || [])),
    products: JSON.parse(JSON.stringify(memberDetails?.products || [])),
  };

  return formData;
};

export const buildCostCalculatorRequest = (
  formData: IFormData,
  memberDetails: IMemberDetails
): ICalculateCostRequest => {
  const request: ICalculateCostRequest = {
    sessionId: memberDetails.sessionId,
    billPlan: formData.requestedBillPlan!,
    membershipLevel: formData.requestedMembershipLevel!,
    members: formData.members!,
    membershipProducts: formData.products!,
    useCache: true,
  };
  return request;
};

export const getIsMembershipChanging = (membershipDetails: IMemberDetails, formData: IFormData) => {
  const { benefitLevels, inRenewal } = membershipDetails;
  const { requestedMembershipLevel } = formData;

  const currentBenefitLevel = benefitLevels?.existingLevel;
  const isChangingBenefitLevel =
    requestedMembershipLevel && currentBenefitLevel !== requestedMembershipLevel ? true : false;

  const isAddingFamilyMembers = formData?.members?.some((m) => m.status === MSSMemberStatus.ADDED);

  const isAddingProductAddOns = formData?.products?.some(
    (p) => p.type === MembershipProductType.RV && p.status === MSSProductStatus.ADDED
  );

  const isDowngradingProductsInRenewal =
    inRenewal &&
    (formData?.members?.some((m) => m.status === MSSMemberStatus.REMOVED) ||
      formData?.products?.some((p) => p.type === MembershipProductType.RV && p.status === MSSProductStatus.REMOVED));

  const isChangingMembership =
    isChangingBenefitLevel || isAddingFamilyMembers || isAddingProductAddOns || isDowngradingProductsInRenewal;
  return isChangingMembership;
};

export const getTodayPacificTimeDateObj = (): Date => {
  const options: any = { timeZone: 'America/Los_Angeles', year: 'numeric', month: 'numeric', day: 'numeric' };
  const formatter = new Intl.DateTimeFormat('en-US', options);

  const now = new Date();
  const formattedToday = formatter.format(now);

  const todayPacific = new Date(formattedToday);

  return todayPacific;
};

export const isPastDue = (dueDateStr: string): boolean => {
  const dueDate = new Date(dueDateStr);
  const todayInPacificTime = getTodayPacificTimeDateObj();

  return todayInPacificTime > dueDate;
};

export const isLaterThanDay = (dueDateStr: string): boolean => {
  const dueDate = new Date(dueDateStr);
  const todayInPacificTime = getTodayPacificTimeDateObj();

  return todayInPacificTime < dueDate;
};

export function getCartProductName<CartProductCode>(value: any): keyof CartProductCode {
  // @ts-ignore
  const keys = Object.keys(CartProductCode).filter((key) => CartProductCode[key as keyof CartProductCode] === value);
  return keys[0] as keyof CartProductCode;
}

export const getIsUpgradingBenefitLevel = (
  currentBenefitLevel: MembershipLevel,
  updatedBenefitLevel: MembershipLevel
) => {
  const classicLevels = [MembershipLevel.CLASSIC, MembershipLevel.CLASSIC_RV];
  const plusLevels = [MembershipLevel.PLUS, MembershipLevel.PLUS_RV];

  const levelsHigherThanClassic = [
    MembershipLevel.PLUS,
    MembershipLevel.PREMIER,
    MembershipLevel.PLUS_RV,
    MembershipLevel.PREMIER_RV,
  ];
  const levelsHigherThanPlus = [MembershipLevel.PREMIER, MembershipLevel.PREMIER_RV];

  const isUpgradingFromClassic =
    classicLevels.includes(currentBenefitLevel) &&
    levelsHigherThanClassic.includes(updatedBenefitLevel as MembershipLevel);
  const isUpgradingFromPlus =
    plusLevels.includes(currentBenefitLevel) && levelsHigherThanPlus.includes(updatedBenefitLevel as MembershipLevel);

  return isUpgradingFromClassic || isUpgradingFromPlus;
};

export const getIsDowngradingBenefitLevel = (
  currentBenefitLevel: MembershipLevel,
  updatedBenefitLevel: MembershipLevel
) => {
  const levelsLowerThanPremier = [
    MembershipLevel.PLUS,
    MembershipLevel.CLASSIC,
    MembershipLevel.CLASSIC_RV,
    MembershipLevel.PLUS_RV,
  ];
  const levelsLowerThanPlus = [MembershipLevel.CLASSIC, MembershipLevel.CLASSIC_RV];
  const plusLevels = [MembershipLevel.PLUS, MembershipLevel.PLUS_RV];
  const premierLevels = [MembershipLevel.PREMIER, MembershipLevel.PREMIER_RV];

  const isDowngradingFromClassic =
    premierLevels.includes(currentBenefitLevel) &&
    levelsLowerThanPremier.includes(updatedBenefitLevel as MembershipLevel);
  const isDowngradingFromPlus =
    plusLevels.includes(currentBenefitLevel) && levelsLowerThanPlus.includes(updatedBenefitLevel as MembershipLevel);

  return isDowngradingFromClassic || isDowngradingFromPlus;
};

export const deleteSessionState = () => {
  sessionStorage.removeItem(STATE_KEY);
};

export const changeAddressClick = () => {
  deleteSessionState();
  window.location.assign(`${getAwsDomainUrl(window.location.hostname)}/insurance/address-change`);
};

export const clearGlobalState = (
  setGlobalState: <StateKey_2 extends keyof IState>(
    stateKey: StateKey_2,
    update: React.SetStateAction<IState[StateKey_2]>
  ) => void
) => {
  for (const stateKey in initialState) {
    const stateVariable = stateKey as keyof IState;
    setGlobalState(stateVariable, initialState[stateVariable]);
  }
};
